import request from '@/config/axios'
import { formatDate } from '@/utils/formatTime'

export interface OssCapacityPackageVO {
  id?: number
  moduleCode: string
  planName: string
  capacityBytes: number
  price: number
  status: number
}

export interface OssCapacityUsageVO {
  tenantId: number
  unlimited: boolean
  ossQuota: number | null
  ossUsed: number
}

export interface OssCapacityOrderRespVO {
  orderId: number
  payOrderId: number
  payStatus: number
  capacityBytes: number
}

export interface OssCapacityOrderPageItemVO {
  orderId: number
  tenantId: number
  tenantName?: string
  moduleCode: string
  planName: string
  capacityBytes: number
  amount: number
  payStatus: number
  payOrderId?: number
  paySuccessTime?: string
  createTime?: string
}

export interface OssCapacityOrderPageReqVO {
  pageNo: number
  pageSize: number
  tenantId?: number
  payStatus?: number
  moduleCode?: string
  createTime?: string[]
}

export interface OssTenantUsageVO {
  tenantId: number
  tenantName?: string
  tenantType?: number
  ossUsed?: number
  ossQuota?: number | null
  usagePercent?: number | null
  currentLevel?: number
  warnLevel?: number
  contactUserId?: number
  contactName?: string
}

export interface OssTenantUsagePageReqVO {
  pageNo: number
  pageSize: number
  name?: string
  externalOnly?: boolean
  usageBand?: string
}

export const getOssCapacityPricingList = () => {
  return request.get<OssCapacityPackageVO[]>({ url: '/saas-billing/oss-capacity/pricing/list' })
}

export const saveOssCapacityPricing = (data: OssCapacityPackageVO) => {
  return request.post({ url: '/saas-billing/oss-capacity/pricing/save', data })
}

export const getOssCapacityPackages = () => {
  return request.get<OssCapacityPackageVO[]>({ url: '/saas-billing/oss-capacity/packages' })
}

export const getOssCapacityUsage = () => {
  return request.get<OssCapacityUsageVO>({ url: '/saas-billing/oss-capacity/usage' })
}

export const createOssCapacityOrder = (pricingId: number) => {
  return request.post<OssCapacityOrderRespVO>({
    url: '/saas-billing/oss-capacity/create-order',
    data: { pricingId }
  })
}

export const getOssCapacityOrderStatus = (orderId: number) => {
  return request.get<OssCapacityOrderRespVO>({
    url: '/saas-billing/oss-capacity/order-status',
    params: { orderId }
  })
}

/** 租户：本户容量订单 */
export const getOssCapacityMyOrderPage = (params: OssCapacityOrderPageReqVO) => {
  return request.get<{ list: OssCapacityOrderPageItemVO[]; total: number }>({
    url: '/saas-billing/oss-capacity/order/page',
    params
  })
}

/** 平台：全部租户容量订单 */
export const getOssCapacityPlatformOrderPage = (params: OssCapacityOrderPageReqVO) => {
  return request.get<{ list: OssCapacityOrderPageItemVO[]; total: number }>({
    url: '/saas-billing/oss-capacity/order/platform-page',
    params
  })
}

/** 平台：租户 OSS 配额一览 */
export const getOssTenantUsagePage = (params: OssTenantUsagePageReqVO) => {
  return request.get<{ list: OssTenantUsageVO[]; total: number }>({
    url: '/saas-billing/oss-capacity/tenant-usage-page',
    params
  })
}

export function payStatusLabel(status?: number): string {
  if (status === 0) return '待支付'
  if (status === 10) return '已支付'
  if (status === 30) return '已关闭'
  return String(status ?? '-')
}

export function payStatusTagType(status?: number): 'warning' | 'success' | 'info' | 'danger' {
  if (status === 0) return 'warning'
  if (status === 10) return 'success'
  if (status === 30) return 'info'
  return 'info'
}

/** 订单时间：兼容毫秒时间戳 / 日期字符串 */
export function formatOrderTime(val?: string | number | null): string {
  if (val === null || val === undefined || val === '') return '-'
  if (typeof val === 'number') {
    return formatDate(new Date(val))
  }
  // 纯数字字符串也按毫秒时间戳处理
  if (/^\d{13}$/.test(String(val))) {
    return formatDate(new Date(Number(val)))
  }
  return formatDate(val)
}

/** bytes → 可读 GB/MB */
export function formatBytes(bytes?: number | null): string {
  if (bytes === null || bytes === undefined) return '不限'
  if (bytes === 0) return '0'
  const gb = bytes / (1024 * 1024 * 1024)
  if (gb >= 1) return `${Number(gb.toFixed(2))} GB`
  const mb = bytes / (1024 * 1024)
  return `${Number(mb.toFixed(2))} MB`
}

export function gbToBytes(gb: number): number {
  return Math.round(gb * 1024 * 1024 * 1024)
}

export function bytesToGb(bytes?: number | null): number | undefined {
  if (bytes === null || bytes === undefined) return undefined
  return Number((bytes / (1024 * 1024 * 1024)).toFixed(4))
}
